import React from "react";
import { Box, Group, Select, Switch, Text, Tooltip } from "@mantine/core";
import {
  NotificationPreferenceSetting,
  StaffNotificationPreferences,
} from "../../../types/notificationPreferences";
import { PreferenceRow } from "./preferenceRows";

const SCOPE_OPTION_PREFIX: Record<string, string> = {
  all: "All",
  own: "Own",
};

const LOCKED_REASON =
  "Only an Owner can change an Owner's notification settings";

export interface NotificationPreferenceCellProps {
  row: PreferenceRow;
  staff: StaffNotificationPreferences;
  /** Edit mode shows controls; view mode shows the value as text. */
  editing: boolean;
  onPreferenceChange: (
    staffUserId: string,
    categoryKey: string,
    setting: NotificationPreferenceSetting,
  ) => void;
  onEmailOptInChange: (staffUserId: string, optedIn: boolean) => void;
  /** Larger touch targets for the mobile card layout. */
  size?: "sm" | "md";
}

const scopeLabel = (row: PreferenceRow, option: string) =>
  `${SCOPE_OPTION_PREFIX[option] ?? option} ${row.label.toLowerCase()}`;

/** The read-mode text for one (row, staff member) value. */
export const getPreferenceReadValue = (
  row: PreferenceRow,
  staff: StaffNotificationPreferences,
): string => {
  if (row.kind === "email") {
    return staff.email_notifications_opt_in ? "On" : "Off";
  }
  const setting = staff.preferences[row.key];
  if (row.category.control === "scope") return scopeLabel(row, setting);
  return setting === "on" ? "On" : "Off";
};

/**
 * One (row, staff member) value, centered. View mode shows it as text; edit
 * mode shows an All / Own select (scope categories) or a switch (toggles and
 * email delivery). Staff the viewer can't edit stay as text, with a tooltip.
 */
export const NotificationPreferenceCell: React.FC<
  NotificationPreferenceCellProps
> = ({
  row,
  staff,
  editing,
  onPreferenceChange,
  onEmailOptInChange,
  size = "sm",
}) => {
  const readValue = (
    <Text fw={500} size="sm">
      {getPreferenceReadValue(row, staff)}
    </Text>
  );

  let content: React.ReactNode;
  if (!editing) {
    content = readValue;
  } else if (!staff.editable) {
    content = (
      <Tooltip label={LOCKED_REASON} withArrow>
        <Box>{readValue}</Box>
      </Tooltip>
    );
  } else {
    const ariaLabel = `${row.label} for ${staff.first_name} ${staff.last_name}`;
    if (row.kind === "email") {
      content = (
        <Switch
          color="teal"
          size={size}
          aria-label={ariaLabel}
          checked={staff.email_notifications_opt_in}
          onChange={(event) =>
            onEmailOptInChange(staff.staff_user_id, event.currentTarget.checked)
          }
        />
      );
    } else if (row.category.control === "scope") {
      content = (
        <Select
          size={size}
          w={size === "md" ? 200 : 180}
          aria-label={ariaLabel}
          allowDeselect={false}
          value={staff.preferences[row.key]}
          data={row.category.options.map((option) => ({
            value: option,
            label: scopeLabel(row, option),
          }))}
          onChange={(value) =>
            value &&
            onPreferenceChange(
              staff.staff_user_id,
              row.key,
              value as NotificationPreferenceSetting,
            )
          }
        />
      );
    } else {
      content = (
        <Switch
          color="teal"
          size={size}
          aria-label={ariaLabel}
          checked={staff.preferences[row.key] === "on"}
          onChange={(event) =>
            onPreferenceChange(
              staff.staff_user_id,
              row.key,
              event.currentTarget.checked ? "on" : "off",
            )
          }
        />
      );
    }
  }

  return (
    <Group justify="center" wrap="nowrap">
      {content}
    </Group>
  );
};

export default NotificationPreferenceCell;
